import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supportTicketService } from "../../../services/supportTicketService";
import { useMessageModal } from "../../contexts/MessageModalContext";
import TicketStatusBadge from "../../../components/support/TicketStatusBadge";
import TicketPriorityBadge from "../../../components/support/TicketPriorityBadge";
import TicketConversation from "../../../components/support/TicketConversation";
import TicketReplyForm from "../../../components/support/TicketReplyForm";
import type {
  AdminUserOption,
  SupportTicket,
  TicketMessage,
  TicketStatus,
} from "../../../types/supportTicket";
import { formatDateTime } from "../../../utils/dateFormat";
import "../../../assets/adminSupportTickets.css";

const AdminSupportTicketDetail: React.FC = () => {
  const { id } = useParams();
  const { showModal } = useMessageModal();

  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loadingReply, setLoadingReply] = useState(false);
  const [loadingAssign, setLoadingAssign] = useState(false);

  const [status, setStatus] = useState<TicketStatus>("Open");
  const [adminUsers, setAdminUsers] = useState<AdminUserOption[]>([]);
  const [assignedAdminId, setAssignedAdminId] = useState<string>("");

  const fetchDetail = async () => {
    if (!id) return;

    try {
      const [ticketRes, messageRes, adminUsersRes] = await Promise.all([
        supportTicketService.getAdminTicketById(id),
        supportTicketService.getAdminTicketMessages(id),
        supportTicketService.getAdminUsers(),
      ]);

      setTicket(ticketRes);
      setMessages(messageRes || []);
      setAdminUsers(adminUsersRes || []);
      setStatus(ticketRes.status);
      setAssignedAdminId(
        ticketRes.assignedAdminId ? String(ticketRes.assignedAdminId) : "",
      );
    } catch (error) {
      console.error("Failed to fetch admin ticket detail", error);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleReply = async (payload: { message: string; files?: File[] }) => {
    if (!id) return;

    try {
      setLoadingReply(true);
      await supportTicketService.adminReplyToTicket(id, payload);
      await fetchDetail();
    } catch (error) {
      console.error("Admin reply failed", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Failed to send reply.",
        confirmText: "OK",
      });
    } finally {
      setLoadingReply(false);
    }
  };

  const handleAssignAndStatusUpdate = async () => {
    if (!id) return;

    try {
      setLoadingAssign(true);

      await supportTicketService.updateTicketStatus(id, {
        status,
        assignedAdminId: assignedAdminId ? Number(assignedAdminId) : null,
      });

      showModal({
        type: "success",
        title: "Success",
        message: "Ticket updated successfully.",
        confirmText: "OK",
      });
      await fetchDetail();
    } catch (error) {
      console.error("Assign/status update failed", error);
      showModal({
        type: "error",
        title: "error",
        message: "Failed to update ticket.",
        confirmText: "OK",
      });
    } finally {
      setLoadingAssign(false);
    }
  };

  if (!ticket) {
    return <div className="admin-ticket-page">Loading ticket detail...</div>;
  }

  return (
    <div className="admin-ticket-page">
      <div className="support-card">
        <div className="ticket-detail-header">
          <div>
            <h2>{ticket.subject}</h2>
            <p>Ticket No: {ticket.ticketNo}</p>
          </div>

          <div className="ticket-detail-badges">
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
          </div>
        </div>

        <div className="ticket-meta-grid">
          <div>
            <strong>Subscriber:</strong> {ticket.subscriberName || "-"}
          </div>
          <div>
            <strong>Email:</strong> {ticket.subscriberEmail || "-"}
          </div>
          <div>
            <strong>Department:</strong> {ticket.department}
          </div>
          <div>
            <strong>Service:</strong> {ticket.service}
          </div>
          <div>
            <strong>Priority:</strong> {ticket.priority}
          </div>
          <div>
            <strong>Created At:</strong> {formatDateTime(ticket.createdAt)}
          </div>
          <div>
            <strong>Assigned To:</strong>{" "}
            {ticket.assignedAdminName || "Not Assigned"}
          </div>
        </div>

        <div className="admin-ticket-actions admin-ticket-actions-grid">
          <div className="admin-field-group">
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as TicketStatus)}
            >
              <option value="Open">Open</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          <div className="admin-field-group">
            <label>Assign To Concerned Admin</label>
            <select
              value={assignedAdminId}
              onChange={(e) => setAssignedAdminId(e.target.value)}
            >
              <option value="">Select Admin User</option>
              {adminUsers.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.name}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-field-group admin-field-group-btn">
            <button
              onClick={handleAssignAndStatusUpdate}
              disabled={loadingAssign}
            >
              {loadingAssign ? "Updating..." : "Update Ticket"}
            </button>
          </div>
        </div>

        <div className="ticket-main-message">
          <h4>Ticket Description</h4>
          <p>{ticket.message}</p>
        </div>

        <TicketConversation messages={messages} />

        {ticket.status !== "Closed" && (
          <div className="ticket-reply-block">
            <h4 className="reply-block-title">Admin Reply</h4>
            <TicketReplyForm
              onSubmit={handleReply}
              loading={loadingReply}
              placeholder="Write admin reply..."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupportTicketDetail;
